<template>
  <div>
    <RevLoadingScreen v-if="isLoading" text="loading" />

    <component
      :is="paymentResultComponent"
      v-if="!isLoading"
      class="bg-static-default-mid"
      :data-qa="`payment-result-${paymentStatus}`"
      :payment="payment"
    />

    <Trustmark />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useTheToast } from '#imports'
import { computed, onErrorCaptured, onMounted, ref, watch } from 'vue'

import { paymentAPI } from '@backmarket/http-api'
import type { PaymentResult } from '@backmarket/http-api/src/api-specs-payment/payment'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useUserStore } from '@backmarket/nuxt-module-oauth/useUserStore'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import Trustmark from '~/scopes/trustmark/Trustmark.vue'

import PaymentFailure from '../../components/Result/PaymentFailure.vue'
import PaymentSuccess from '../../components/Result/PaymentSuccess.vue'
import useEnhanceLogError from '../../composables/useEnhancedLogError'
import { useCartStore } from '../../stores/cartStore'

import { usePaymentResultEventTracker } from './usePaymentResultEventTracker'

const cartStore = useCartStore()
const userStore = useUserStore()
const route = useRoute()
const { openErrorToast } = useTheToast()
const errorLogger = useEnhanceLogError()

const {
  data: payment,
  error: paymentError,
  pending: isLoading,
} = await useHttpFetch(paymentAPI.getResult, {
  pathParams: { paymentId: route.params.paymentId },
})

const paymentResult = ref(payment.value as PaymentResult)

const paymentResultComponent = computed(() => {
  if (!paymentResult.value) {
    return null
  }

  return paymentResult.value.success ? PaymentSuccess : PaymentFailure
})

const paymentStatus = computed(() => {
  if (!paymentResult.value) {
    return 'loading'
  }

  return paymentResult.value.success ? 'success' : 'failure'
})

onMounted(async () => {
  if (paymentError.value) {
    return
  }

  const isSuccess = paymentResult.value.success
  const track = usePaymentResultEventTracker(paymentResult.value)

  if (isSuccess) {
    track.paymentSuccess()
  } else {
    track.paymentFailure()
  }

  await cartStore.fetchCart()
  await userStore.fetchUser()
})

watch(
  paymentError,
  (error) => {
    if (error) {
      openErrorToast()
    }
  },
  {
    immediate: true,
  },
)

onErrorCaptured((err, instance, info) => {
  // eslint-disable-next-line no-underscore-dangle
  const componentTag = instance?.$options?.__name || 'none'

  errorLogger({
    component: {
      name: componentTag,
      props: { ...instance?.$props },
    },
    routeName: route.name,
    err,
    info,
  })

  return true
})
</script>
